import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AllBookingLists, Answers, BookingLists, BookingStats, IAnswerQuestion } from "../interfaces/BookingList";
import apiClient from "../api/apiClient";

interface BookingState {
  loading: boolean;
  user: any | null;
  error: string | null;
  isLoggedIn: boolean;
  data: any[] | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  stats?: BookingStats;
  data1: Answers[] | null;
}

const initialState: BookingState = {
  loading: false,
  user: null,
  error: null,
  isLoggedIn: false,
  data: null,
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
  stats: undefined,
  data1: null,
};

export const BookingList = createAsyncThunk(
  "booking/BookingList",
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get('/api/v1/bookings');
      return response.data.data.userBookings;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
  }
);

export const BookingDetails = createAsyncThunk(
  "booking/BookingDetails",
  async (bookingId: string, thunkAPI) => {
    try {
      const response = await apiClient.get(`/api/v1/patients/userBookingDetails/${bookingId}`);
      return response.data.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
  }
);

export const BookQuestion = createAsyncThunk(
  "booking/BookQuestion",
  async ({ answers, programId }: IAnswerQuestion, thunkAPI) => { 
    try {
      const response = await apiClient.post('/api/v1/patients/answers', {
        programId: programId,
        questions: answers,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Update failed: ${error.message}`);
    }
  }
);

export const BookAnsweredData = createAsyncThunk(
  "booking/BookAnsweredData",
  async (pid: string, thunkAPI) => {
    try {
      const response = await apiClient.get(`/api/v1/patients/answers/${pid}`);
      return response.data.data.answers;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Update failed: ${error.message}`);
    }
  }
);

export const BookSlot = createAsyncThunk(
  "booking/BookSlot",
  async (bookslots: any, thunkAPI) => {
    try {
      const response = await apiClient.post('/api/v1/patients/counsellings/book', {
        id: bookslots,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Update failed: ${error.message}`);
    }
  }
);

export const AllBookingList = createAsyncThunk(
  "booking/AllBookingList",
  async (params: { page?: number; limit?: number; search?: string; status?: string } | undefined, thunkAPI) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 10;
      const search = params?.search ? params.search.trim() : "";
      const status = params?.status && params.status !== "all" ? params.status : "";
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      });
      const response = await apiClient.get(`/api/v1/bookings/all?${queryParams.toString()}`);
      return response.data.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
    }
  }
);

export const exportAllBookings = createAsyncThunk(
  "booking/exportAllBookings",
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get('/api/v1/bookings/all?limit=1000&page=1');
      const data = response.data?.data;
      return (data?.bookings || data || []) as AllBookingLists[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(`Export failed: ${error.message}`);
    }
  }
);


const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(BookingList.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error on pending
      })
      .addCase(BookingList.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload; 
      })
      .addCase(BookingList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error("Error:", action.payload); // Log the detailed error
      })
      .addCase(BookingDetails.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error on pending
      })
      .addCase(BookingDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload; 
      })
      .addCase(BookingDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error("Error:", action.payload); // Log the detailed error
      })
      .addCase(AllBookingList.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error on pending
      })
      .addCase(AllBookingList.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && action.payload.bookings) {
          state.data = action.payload.bookings;
          state.total = action.payload.total || action.payload.bookings.length;
          state.page = action.payload.page || 1;
          state.limit = action.payload.limit || 10;
          state.totalPages = action.payload.totalPages || 1;
          state.stats = action.payload.stats;
        } else if (Array.isArray(action.payload)) {
          state.data = action.payload;
          state.total = action.payload.length;
          state.page = 1;
          state.limit = action.payload.length || 10;
          state.totalPages = 1;
        }
      })
      .addCase(AllBookingList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error("Error:", action.payload); // Log the detailed error
      })
      .addCase(BookSlot.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error on pending
      })
      .addCase(BookSlot.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload; 
      })
      .addCase(BookSlot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error("Error:", action.payload); // Log the detailed error
      }) .addCase(BookAnsweredData.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error on pending
      })
      .addCase(BookAnsweredData.fulfilled, (state, action) => {
        state.loading = false;
        state.data1 = action.payload; 
      })
      .addCase(BookAnsweredData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error("Error:", action.payload); // Log the detailed error
      });
      
  },
});

export const { resetState } = bookingSlice.actions;
export default bookingSlice.reducer;
